// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file MoreMenu.tsx
 * @input Uses DropdownMenu, getIcon
 * @output Exports MoreMenu component and MoreMenuProps type
 * @position Core implementation; consumed by index.ts
 *
 * Overflow menu with a three-dot icon trigger. A thin wrapper around
 * DropdownMenu with icon-only button defaults.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/MoreMenu/MoreMenu.test.tsx
 * - /packages/core/src/MoreMenu/index.ts
 * - /apps/storybook/stories/MoreMenu.stories.tsx
 * - /packages/cli/templates/blocks/components/MoreMenu/ (showcase blocks)
 */
import { getIcon } from "../Icon/globalIconRegistry.js";
import { DropdownMenu } from "../DropdownMenu/DropdownMenu.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { stableClassName } from "../naming.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Overflow menu with a three-dot icon trigger.
 *
 * A convenience wrapper around DropdownMenu with icon-only button defaults.
 *
 * @example
 * ```
 * <MoreMenu
 *   items={[
 *     { label: 'Edit', onClick: handleEdit },
 *     { label: 'Delete', onClick: handleDelete },
 *   ]}
 * />
 * ```
 */
export function MoreMenu({
  items,
  label: labelFromProps,
  variant = 'ghost',
  size: sizeProp,
  icon,
  isDisabled = false,
  isMenuOpen,
  onOpenChange,
  xstyle,
  className: classNameProp,
  style,
  'data-testid': testId,
  ref
}) {
  const t = useTranslator();
  const label = labelFromProps ?? t('@astryx.moreMenu.label');
  const size = useSize(sizeProp, 'md');
  const moreIcon = getIcon('moreHorizontal');
  return /*#__PURE__*/_jsx(DropdownMenu, {
    className: classNameProp ? `${stableClassName('more-menu')} ${classNameProp}` : stableClassName('more-menu'),
    xstyle: xstyle,
    style: style,
    isMenuOpen: isMenuOpen,
    onOpenChange: onOpenChange,
    button: {
      label,
      icon: icon ?? moreIcon,
      variant,
      size,
      isDisabled,
      tooltip: label,
      isIconOnly: true,
      ref
    },
    items: items,
    hasChevron: false,
    "data-testid": testId
  });
}
MoreMenu.displayName = 'MoreMenu';